import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { Select } from 'shared/components'
import { useFetchInfinite } from 'shared/hooks'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { Field, fieldButtonClassName } from '../Field'
import { Position, usePlacement } from '../../placement'
import { useAreaValidationSchema } from './hooks/useAreaValidationSchema'

type AreaProps = {
	floormapConfig: any[]
	onSubmit: (data: any) => void
}

export const Area = ({ floormapConfig, onSubmit }: AreaProps) => {
	const { t } = useTranslation()
	const storeCtx = useContext(StoreSettingsContext)
	const validationResolver = useAreaValidationSchema()

	const {
		handleSubmit,
		control,
		setError,
		formState: { errors }
	} = useForm({
		resolver: validationResolver,
		mode: 'onChange'
	})

	const {
		data: booths,
		fetchNextPage: fetchNextAreas,
		isLoading: loadingAreas
	} = useFetchInfinite({
		name: 'fetchAreas',
		endpoint: '/booths/'
	})

	const boothOptions = (booths?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((booth: any) => ({ label: booth.number, value: booth.pk }))

	const submitArea = (data: any, position: Position = { x: 45, y: 70 }) => {
		const id = data.booth.label.toString()

		if (floormapConfig.some((item: any) => item.id === id)) {
			return setError('booth', {
				type: 'custom',
				message: t('Area number is not unique')
			})
		}

		onSubmit([
			...floormapConfig,
			{
				type: 'booth',
				...position,
				width: 20,
				height: 20,
				fill: storeCtx?.primaryColor,
				draggable: true,
				id
			}
		])
	}

	usePlacement('booth', (position) => handleSubmit((data) => submitArea(data, position))())

	return (
		<Field label={t('Areas')} section="booth" isPlaceable>
			<form className="space-y-2" onSubmit={handleSubmit((data) => submitArea(data))}>
				<Controller
					name="booth"
					control={control}
					render={({ field }) => (
						<Select
							id="floor-map-booth"
							variant="form"
							label={t('Area number')}
							placeholder={t('Area number')}
							options={boothOptions}
							isLoading={loadingAreas}
							handleInfiniteScroll={fetchNextAreas}
							error={(errors as any).booth?.label ?? (errors as any).booth}
							{...field}
						/>
					)}
				/>
				<button type="submit" className={fieldButtonClassName}>
					{t('Add area')}
				</button>
			</form>
		</Field>
	)
}
